// src/services/firebase/portfolioService.js
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from './config';

export const savePortfolioTargets = async (userEmail, data, selectedPortfolioId = 'default') => {
  if (!userEmail) throw new Error('Email gerekli');
  
  try {
    const batch = writeBatch(db);
    const portfolioRef = collection(db, 'users', userEmail, 'portfolios', selectedPortfolioId, 'portfolio');
    
    Object.entries(data).forEach(([key, value]) => {
      const docRef = doc(portfolioRef, key);
      batch.set(docRef, {
        ...value,
        name: key,
        updatedAt: new Date(),
        lastModifiedBy: userEmail
      });
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Portfolio kaydetme hatası:', error);
    throw error;
  }
};

export const getPortfolioTargets = async (userId, selectedPortfolioId = 'default') => {
  if (!userId) throw new Error('User ID is required');

  try {
    const portfolioRef = collection(db, 'users', userId, 'portfolios', selectedPortfolioId, 'portfolio');
    const snapshot = await getDocs(portfolioRef);
    
    const portfolioData = {};
    snapshot.forEach(doc => {
      portfolioData[doc.id] = doc.data();
    });
    return portfolioData;
  } catch (error) {
    console.error('Portfolio getirme hatası:', error);
    throw new Error(error.message || 'Portfolio verileri getirilemedi');
  }
};

export const updateCategory = async (userId, categoryId, categoryData, selectedPortfolioId = 'default') => {
  if (!userId || !categoryId) throw new Error('User ID and category ID are required');

  try {
    // Veri doğrulama
    if (typeof categoryData !== 'object') {
      throw new Error('Invalid category data format');
    }

    const docRef = doc(db, 'users', userId, 'portfolios', selectedPortfolioId, 'portfolio', categoryId);
    await setDoc(docRef, {
      ...categoryData,
      updatedAt: new Date(),
      lastModifiedBy: userId
    });
    console.log('Category updated successfully:', categoryId);
    return true;
  } catch (error) {
    console.error('Kategori güncelleme hatası:', error);
    throw new Error(error.message || 'Kategori güncellenirken bir hata oluştu');
  }
};

export const deleteCategory = async (userId, categoryId, selectedPortfolioId = 'default') => {
  if (!userId || !categoryId) throw new Error('User ID and category ID are required');

  try {
    const docRef = doc(db, 'users', userId, 'portfolios', selectedPortfolioId, 'portfolio', categoryId);
    await deleteDoc(docRef);
    console.log('Category deleted successfully:', categoryId);
    return true;
  } catch (error) {
    console.error('Kategori silme hatası:', error);
    throw new Error(error.message || 'Kategori silinirken bir hata oluştu');
  }
};

export const getUserData = async (userId, selectedPortfolioId = 'default') => {
  if (!userId) throw new Error('User ID is required');

  try {
    const docRef = doc(db, 'users', userId, 'portfolios', selectedPortfolioId, 'settings', 'rebalance');
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? snapshot.data() : {};
  } catch (error) {
    console.error('Kullanıcı verisi getirme hatası:', error);
    throw new Error(error.message || 'Kullanıcı verileri getirilemedi');
  }
};

export const updateRebalanceSettings = async (userId, settings, selectedPortfolioId = 'default') => {
  if (!userId) throw new Error('User ID is required');
  if (!settings || typeof settings !== 'object') {
    throw new Error('Invalid settings format');
  }

  try {
    const docRef = doc(db, 'users', userId, 'portfolios', selectedPortfolioId, 'settings', 'rebalance');
    await setDoc(docRef, {
      ...settings,
      updatedAt: new Date()
    }, { merge: true });
    console.log('Rebalance settings updated successfully');
    return true;
  } catch (error) {
    console.error('Rebalance ayarları güncelleme hatası:', error);
    throw new Error(error.message || 'Rebalance ayarları güncellenirken bir hata oluştu');
  }
};

export const moveAsset = async (userId, selectedPortfolioId, symbol, targetCategoryName, targetSubCategoryName = null) => {
  if (!userId || !selectedPortfolioId || !symbol) return;
  
  const portfolioRef = collection(db, 'users', userId, 'portfolios', selectedPortfolioId, 'portfolio');
  const batch = writeBatch(db);

  try {
     const snapshot = await getDocs(portfolioRef);
     
     // 1. Önce varlığın mevcut konumunu bul ve sil
     snapshot.forEach(doc => {
         const data = doc.data();
         let changed = false;

         // Ana kategoride mi?
         if (data.assets && data.assets[symbol]) {
             delete data.assets[symbol];
             changed = true;
         }

         // Alt kategoride mi?
         if (data.subCategories) {
             Object.keys(data.subCategories).forEach(subKey => {
                 if (data.subCategories[subKey].assets && data.subCategories[subKey].assets[symbol]) {
                     delete data.subCategories[subKey].assets[symbol];
                     changed = true;
                 }
             });
         }
         
         if (changed) {
             batch.set(doc.ref, data);
         }
     });

     // 2. Yeni konuma ekle
     // Hedef kategori dökümanını bul
     let targetDoc = snapshot.docs.find(d => d.id === targetCategoryName);
     let targetData = targetDoc ? targetDoc.data() : { name: targetCategoryName, allocation: 0 };
     
     if (!targetData.assets) targetData.assets = {};
     
     // Varlık verisini hazırla (Basitçe allocation 0 olarak ekle, detaylar import'tan gelecek zaten)
     const newItem = {
         allocation: 0,
         symbol: symbol
     };

     if (targetSubCategoryName) {
         if (!targetData.subCategories) targetData.subCategories = {};
         if (!targetData.subCategories[targetSubCategoryName]) {
             targetData.subCategories[targetSubCategoryName] = { 
                 name: targetSubCategoryName, 
                 allocation: 0,
                 assets: {} 
             };
         }
         if (!targetData.subCategories[targetSubCategoryName].assets) {
             targetData.subCategories[targetSubCategoryName].assets = {};
         }
         targetData.subCategories[targetSubCategoryName].assets[symbol] = newItem;
     } else {
         targetData.assets[symbol] = newItem;
     }

     const targetDocRef = targetDoc ? targetDoc.ref : doc(portfolioRef, targetCategoryName);
     batch.set(targetDocRef, targetData);

     await batch.commit();
     return true;

  } catch (error) {
      console.error('Varlık taşıma hatası:', error);
      throw error;
  }
};

export const updatePortfolioStructure = async (userEmail, portfolioId, items) => {
  if (!userEmail || !portfolioId || !items) throw new Error('Missing required arguments');

  try {
    const portfolioRef = collection(db, 'users', userEmail, 'portfolios', portfolioId, 'portfolio');

    // Archive Logic ile birlikte işlemleri sırayla yapmamız gerekebilir, 
    // bu yüzden batch yerine sıralı işlemler veya batch'i dikkatli kullanmalıyız.
    // Ancak Archive için "Move others to Zake" mantığını batch içinde yapabiliriz.
    
    // Zake kategorisini hazırlayalım (veya referansını alalım)
    const zakeDocRef = doc(portfolioRef, 'Zake');
    let zakeOperation = null; // Zake güncellemesi gerekirse buraya koyacağız

    // Mevcut Zake verisini almak için, loop öncesi tüm kategorileri çekmek daha güvenli
    // ancak performans için sadece update edilecek kategorilere bakacağız.
    // Kullanıcının isteği: "bu yukarıdakiler dışında smart investün içnde hisse varsa onu zake grubuna aktaracak"
    // Bu yüzden önce mevcut "Smart Investor" (veya items içindeki kategoriler) verisini okumalıyız.

    const batch = writeBatch(db);

    // Zake için birikmiş varlıkları tutacak geçici depo
    let assetsToMoveToZake = {};

    for (const category of items) {
        if (!category.name) continue;

        const docRef = doc(portfolioRef, category.name);
        
        // Mevcut kategoriyi oku (Archive mantığı için)
        let existingAssets = new Set();
        try {
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.assets) {
                    Object.keys(data.assets).forEach(k => existingAssets.add(k));
                }
                // Alt kategorilerdeki varlıkları da kontrol etmeli miyiz? 
                // İsteğe göre "Smart Investor içindeki hisseler" dendiği için top-level assets'e bakıyoruz.
            }
        } catch (e) {
            console.warn("Mevcut kategori okunamadı:", e);
        }

        // Convert array-based structure from JSON back to Object/Map-based structure for Firestore
        const subCategoriesMap = {};
        if (category.subCategories && Array.isArray(category.subCategories)) {
            category.subCategories.forEach(sub => {
                const subAssets = {};
                if (sub.items && Array.isArray(sub.items)) {
                    sub.items.forEach(asset => {
                         subAssets[asset.symbol] = {
                             allocation: asset.targetAllocation || 0
                         };
                    });
                }

                subCategoriesMap[sub.name] = {
                    allocation: sub.targetAllocation || 0,
                    assets: subAssets
                };
            });
        }

        const assetsMap = {};
        const newAssetSymbols = new Set();

        if (category.items && Array.isArray(category.items)) { 
            category.items.forEach(asset => {
                assetsMap[asset.symbol] = {
                    allocation: asset.targetAllocation || 0
                };
                newAssetSymbols.add(asset.symbol);
            });
        }
        
        // FARK HESAPLAMA: Eskiden olup yeni listede olmayanları bul
        existingAssets.forEach(existingSymbol => {
            if (!newAssetSymbols.has(existingSymbol)) {
                // Bu sembol siliniyor, Zake'ye taşı
                assetsToMoveToZake[existingSymbol] = {
                    allocation: 0 // Zake içinde allocation 0 olsun veya varsayılan
                };
            }
        });

        const docData = {
            name: category.name,
            allocation: category.targetAllocation || 0,
            subCategories: subCategoriesMap,
            assets: assetsMap,
            updatedAt: new Date()
        };

        batch.set(docRef, docData, { merge: true }); // Merge true veya false? JSON tam yapıyı içerdiği için replace etmek daha doğru olabilir ama merge kullanıyoruz.
        // DİKKAT: Merge true kullanırsak, eski 'assets' içindeki silinenler SİLİNMEZ, kalır.
        // Bu yüzden eğer temiz bir yapı istiyorsak, ve "Zake'ye taşıyorsak", merge kullanmamalıyız 
        // VEYA assets alanını tamamen override etmeliyiz.
        // batch.set(..., {merge:true}) ile assets: assetsMap verirsek, map içindeki keyler update olur, EKSİK keyler kalmaya devam eder (Firestore map merge behaving).
        // HAYIR: Firestore set(..., {merge:true}) top leve field replacement yapmaz, recursive merge yapar.
        // Eğer assets: { A:1 } verip DB'de assets: { A:1, B:1 } varsa, sonuç { A:1, B:1 } olur. B silinmez.
        // Silmek için { "assets.B": deleteField() } yapmak gerekir.
        // VEYA merge: false kullanıp tüm dökümanı baştan yazmak gerekir.
        // JSON import PORTFOLIO YAPILANDIRMASI olduğu için genelde "overwrite" beklenir.
        // O yüzden bu kategori için `merge: false` (veya merge option vermeden set) kullanacağız, böylece tam olarak JSON'daki yapı geçerli olacak.
        // Ancak `allocation` vb. diğer alanları korumak isteyebiliriz? Hayır, JSON master data.
        
        // KARAR: batch.set(docRef, docData) -> Overwrite. Eski veri gider.
        // Bu sayede "Zake'ye taşıma" mantığımız işe yarar çünkü burada sildiğimiz (yazmadığımız) veri gerçekten silinmiş olur, Zake'de yedeğini oluşturacağız.
        batch.set(docRef, docData); 
    }

    // Eğer Zake'ye taşınacak varlıklar varsa
    if (Object.keys(assetsToMoveToZake).length > 0) {
        // Zake dökümanını oku (veya oluştur)
        const zakeRef = doc(portfolioRef, 'Zake');
        let zakeData = { name: 'Zake', allocation: 0, assets: {} };
        
        try {
            const zakeSnap = await getDoc(zakeRef);
            if (zakeSnap.exists()) {
                zakeData = zakeSnap.data();
                if (!zakeData.assets) zakeData.assets = {};
            }
        } catch(e) {}

        // Yeni taşınanları ekle (Zake'de zaten varsa üzerine yazmaz veya yazar, fark etmez)
        Object.assign(zakeData.assets, assetsToMoveToZake);
        
        batch.set(zakeRef, zakeData);
    }

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Portfolio yapılandırma güncelleme hatası:', error);
    throw error;
  }
};